import React, { createContext, useContext, useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';

const AuthContext = createContext({})

export const useAuth = () => {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider')
  }
  return context
}

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null)
  const [loading, setLoading] = useState(false)
  const [isInitialized, setIsInitialized] = useState(false)

  // Simple initialization - no complex logic
  useEffect(() => {
    console.log('AuthContext: Simple initialization starting...')
    
    const initializeAuth = async () => {
      try {
        // Check if there's an existing session
        const { data: { session } } = await supabase.auth.getSession()
        
        if (session?.user) {
          console.log('Found existing session, restoring user state')
          setUser(session.user)
          
          // Store user info in localStorage for simple auth checks
          localStorage.setItem('currentUser', JSON.stringify({
            id: session.user.id,
            email: session.user.email,
            role: session.user.user_metadata?.role || 'viewer'
          }))
        } else {
          console.log('No existing session found, clearing data')
          // Clear any existing data
          localStorage.removeItem('currentUser')
          sessionStorage.removeItem('currentUser')
          setUser(null)
        }
        
        setLoading(false)
        setIsInitialized(true)
        
        console.log('AuthContext: Simple initialization complete')
      } catch (error) {
        console.error('Error during auth initialization:', error)
        // Clear data on error
        localStorage.removeItem('currentUser')
        sessionStorage.removeItem('currentUser')
        setUser(null)
        setLoading(false)
        setIsInitialized(true)
      }
    }

    initializeAuth()

    // Set up simple auth state change listener
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      console.log('Auth state change:', event, session?.user?.id)
      
      if (event === 'SIGNED_IN' && session?.user) {
        setUser(session.user)
        localStorage.setItem('currentUser', JSON.stringify({
          id: session.user.id,
          email: session.user.email,
          role: session.user.user_metadata?.role || 'viewer'
        }))
      } else if (event === 'SIGNED_OUT') {
        setUser(null)
        localStorage.removeItem('currentUser')
        sessionStorage.removeItem('currentUser')
      }
    })

    return () => subscription?.unsubscribe()
  }, [])

  // Add event listeners for automatic logout when tab is closed
  useEffect(() => {
    // Set a timestamp when the page loads
    const pageLoadTime = Date.now()
    sessionStorage.setItem('pageLoadTime', pageLoadTime.toString())

    const handleTabClose = () => {
      console.log('Tab closing, logging out user')
      // Clear user data
      localStorage.removeItem('currentUser')
      sessionStorage.removeItem('currentUser')
      
      // Sign out from Supabase
      supabase?.auth?.signOut()
    }

    const handleBeforeUnload = (event) => {
      console.log('beforeunload triggered')
      // Store the current time
      sessionStorage.setItem('beforeUnloadTime', Date.now().toString())
    }

    const handlePageHide = () => {
      console.log('pagehide triggered')
      const beforeUnloadTime = sessionStorage.getItem('beforeUnloadTime')
      const pageLoadTime = sessionStorage.getItem('pageLoadTime')
      
      if (beforeUnloadTime && pageLoadTime) {
        const timeDiff = parseInt(beforeUnloadTime) - parseInt(pageLoadTime)
        // If the time difference is very small, it's likely a refresh
        // If it's larger, it's likely a tab close
        if (timeDiff > 1000) { // More than 1 second
          console.log('Tab close detected, logging out')
          handleTabClose()
        } else {
          console.log('Page refresh detected, keeping session')
        }
      }
    }

    // Add event listeners
    window.addEventListener('beforeunload', handleBeforeUnload)
    window.addEventListener('pagehide', handlePageHide)

    return () => {
      // Clean up
      window.removeEventListener('beforeunload', handleBeforeUnload)
      window.removeEventListener('pagehide', handlePageHide)
    }
  }, [])

  // Simple sign in - no complex profile fetching
  const signIn = async (email, password) => {
    try {
      setLoading(true)
      const { data, error } = await supabase?.auth?.signInWithPassword({
        email,
        password
      })

      if (error) throw error
      
      if (data?.user) {
        setUser(data.user)
        
        // Store user info in localStorage for simple auth checks
        localStorage.setItem('currentUser', JSON.stringify({
          id: data.user.id,
          email: data.user.email,
          role: data.user.user_metadata?.role || 'viewer'
        }))
      }
      
      return { data, error: null }
    } catch (error) {
      console.error('Error signing in:', error)
      return { data: null, error }
    } finally {
      setLoading(false)
    }
  }

  // Simple sign out
  const signOut = async () => {
    try {
      setLoading(true)
      await supabase?.auth?.signOut()
      
      setUser(null)
      localStorage.removeItem('currentUser')
      sessionStorage.removeItem('currentUser')
      
      return { error: null }
    } catch (error) {
      console.error('Error signing out:', error)
      return { error }
    } finally {
      setLoading(false)
    }
  }

  // Simple value object - only essential functions
  const value = {
    user,
    userProfile: null, // Always null to prevent profile-related loops
    loading,
    signIn,
    signOut,
    isInitialized
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}
